'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import { useRouter, usePathname } from 'next/navigation';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import UserSettingsDialog from '@/components/dialogs/UserSettingsDialog';
import { ArrowLeft } from 'lucide-react';
import { AuthenticationDialog } from '../dialogs/AuthenticationDialog';
import { useAuth } from '../providers/AuthProvider';

const pages = [
  {
    value: 'ai-image-creator',
    label: 'AI Image Creator',
    path: '/image-creator',
    icon: '/image-creator.svg',
    title: (
      <>
        <span className='bg-gradient-to-r from-fuchsia-400 via-fuchsia-500 to-fuchsia-400 bg-clip-text text-transparent drop-shadow-[0_0_15px_rgba(217,70,239,0.8)]'>
          AI IMAGE
        </span>{' '}
        <span className='text-stone-50 drop-shadow-[0_0_8px_rgba(255,255,255,0.4)]'>
          GENERATOR
        </span>
      </>
    ),
  },
  {
    value: 'model-trainer',
    label: 'Model Trainer',
    path: '/model-trainer',
    icon: '/model-trainer.svg',
    title: (
      <>
        <span className='bg-gradient-to-r from-fuchsia-400 via-fuchsia-500 to-fuchsia-400 bg-clip-text text-transparent drop-shadow-[0_0_15px_rgba(217,70,239,0.8)]'>
          MODEL
        </span>{' '}
        <span className='text-stone-50 drop-shadow-[0_0_8px_rgba(255,255,255,0.4)]'>
          TRAINING
        </span>
      </>
    ),
  },
  {
    value: 'history',
    label: 'History',
    path: '/history',
    icon: '/image-creator.svg',
    title: (
      <>
        <span className='bg-gradient-to-r from-fuchsia-400 via-fuchsia-500 to-fuchsia-400 bg-clip-text text-transparent drop-shadow-[0_0_15px_rgba(217,70,239,0.8)]'>
          GENERATE
        </span>{' '}
        <span className='text-stone-50 drop-shadow-[0_0_8px_rgba(255,255,255,0.4)]'>
          HISTORY
        </span>
      </>
    ),
  },
];

export default function Header() {
  const router = useRouter();
  const pathname = usePathname();
  const currentPage = pages.find((p) => p.path === pathname) || pages[0];
  const [isUserSettingsOpen, setIsUserSettingsOpen] = useState(false);
  const { user } = useAuth();
  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);

  return (
    <>
      <Card className='relative w-full h-20 px-8 py-3 bg-zinc-950 border-b border-slate-700/50 overflow-hidden rounded-none'>
        {/* Left - Logo + Select */}
        <div className='absolute left-8 top-1/2 -translate-y-1/2 flex items-center gap-8 z-20'>
          <div className='flex items-center gap-2'>
            <div className='bg-zinc-500/60 rounded-full p-2 hover:bg-zinc-500/80 transition-colors cursor-pointer'>
              <ArrowLeft className='w-4 h-4 text-white' />
            </div>
            <div
              className='relative w-[150px] h-[40px] cursor-pointer'
              onMouseDown={() => router.push('/')}
            >
              <Image src='/logo.svg' alt='Logo' fill />
            </div>
          </div>

          <Select
            value={currentPage.value}
            onValueChange={(value) => {
              const selected = pages.find((p) => p.value === value);
              if (selected) router.push(selected.path);
            }}
          >
            <SelectTrigger className='w-52 !h-8 bg-neutral-800/80 border-neutral-600/50 text-white text-sm rounded-sm backdrop-blur-sm cursor-pointer'>
              <SelectValue>
                <div className='flex items-center gap-2'>
                  <Image
                    src={currentPage.icon}
                    alt={currentPage.label}
                    width={16}
                    height={16}
                  />
                  <span>{currentPage.label}</span>
                </div>
              </SelectValue>
            </SelectTrigger>
            <SelectContent className='bg-neutral-800 border-neutral-600/50 backdrop-blur-sm'>
              {pages.map((page) => (
                <SelectItem
                  key={page.value}
                  value={page.value}
                  className='text-white focus:bg-neutral-700 text-sm cursor-pointer'
                >
                  <div className='flex items-center gap-2'>
                    <Image
                      src={page.icon}
                      alt={page.label}
                      width={16}
                      height={16}
                    />
                    <span>{page.label}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* <Link
            href='/favourites'
            className='flex items-center gap-2 leading-tight text-slate-100 font-semibold'
          >
            Favourites
          </Link> */}
        </div>

        {/* Center Title with glow */}
        <div className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10'>
          <div className='absolute inset-0 flex justify-center items-center pointer-events-none'>
            <div className='w-[400px] h-16 bg-gradient-to-r from-transparent via-fuchsia-600/5 to-transparent rounded-full blur-2xl' />
          </div>
          <h1 className='text-2xl font-bold tracking-wider whitespace-nowrap uppercase font-channey'>
            {currentPage.title}
          </h1>
        </div>

        {/* Right - Credits, Upgrade, Avatar */}
        {user ? (
          <div className='absolute right-8 top-1/2 -translate-y-1/2 flex items-center gap-4 z-20'>
            <Badge
              variant='secondary'
              className='w-14 h-8 p-2 bg-gray-100/20 hover:bg-gray-100/25 text-white border-0 transition-colors rounded-full inline-flex items-center gap-1'
            >
              <Image src='/coin.svg' alt='Coin' width={12} height={12} />
              <span className='text-sm font-normal'>150</span>
            </Badge>
            <Button
              onMouseDown={() => {
                router.push('/subscription');
              }}
              size='sm'
              className={cn(
                'bg-fuchsia-glow !pl-6 !pr-5',
                'hover:from-fuchsia-700 hover:to-fuchsia-800',
                'text-white font-medium transition-all duration-200',
                'shadow-lg hover:shadow-fuchsia-500/25',
                'border-0 rounded-full text-sm',
              )}
            >
              Upgrade
              <Zap className='w-4 h-4 text-white fill-white' />
            </Button>

            <Avatar
              className='size-8 cursor-pointer'
              onMouseDown={() => setIsUserSettingsOpen(true)}
            >
              <AvatarImage
                src={
                  user.user_metadata?.avatar_url ??
                  'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face'
                }
                alt='User avatar'
              />
              <AvatarFallback className='bg-neutral-700 text-white text-sm'>
                {user.user_metadata?.username?.[0]?.toUpperCase() ?? 'U'}
              </AvatarFallback>
            </Avatar>
          </div>
        ) : (
          <div className='absolute right-8 top-1/2 -translate-y-1/2 flex items-center gap-4 z-20'>
            <Button
              size='sm'
              className='bg-fuchsia-glow hover:from-fuchsia-700 hover:to-fuchsia-800 hover:shadow-fuchsia-500/25  text-white px-4 py-1.5 rounded-full text-sm font-medium'
              onMouseDown={() => setIsAuthDialogOpen(true)}
            >
              Sign In
            </Button>
          </div>
        )}
      </Card>

      <AuthenticationDialog
        isOpen={isAuthDialogOpen}
        onClose={() => setIsAuthDialogOpen(false)}
      />
      <UserSettingsDialog
        open={isUserSettingsOpen}
        onOpenChange={setIsUserSettingsOpen}
      />
    </>
  );
}
